/* eslint-disable @typescript-eslint/no-explicit-any */
import * as Sentry from '@sentry/react';

import { Database } from './database.types';

import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Trace is a wrapper around Sentry Breadcrumb.
 * This is a way to add event information that will not be sent unless an error happens.
 * @param scope defines component/block/screen scope or event type such as `3d.controls`, `navigation.userSettings`
 * @param message short description of the event
 */
export const addTrace = (scope: string, message: string) =>
    Sentry.addBreadcrumb({
        level: 'info',
        category: scope,
        message,
    });

/**
 * Tags are way to markup important information about the current UI context.
 * What booth the user is logged in to? What activity they are viewing? Etc.
 * @param key `entity` description such as 'booth name', 'activity id'
 * @param value value of the `entity`
 */
export const addTag = (key: string, value: string | number | null) => {
    Sentry.setTag(key, value);
};

/**
 * Global logger API. Currently we only have two types of manual logs.
 * Warn and Info, Errors should be handled by our error handling flow AND Sentry automatically.
 * @method warn network errors (like fetch, etc)
 * @method info important usage logs that could be useful for getting extra information
 */
export const Logger = {
    error: (message: string) => {
        console.error(message);
        Sentry.captureMessage(message, 'error');
    },
    warn: (message: string) => {
        console.warn(message);
        Sentry.captureMessage(message, 'warning');
    },
    info: (message: string) => {
        // eslint-disable-next-line no-console
        console.info(message);
        Sentry.captureMessage(message, 'info');
    },
};

type SyncFn<Args extends any[], R> = (...args: Args) => R;
type AsyncFn<Args extends any[], R> = (...args: Args) => Promise<R>;
type AnyFn<Args extends any[], R> = (...args: Args) => R | Promise<R>;

const OMIT_ERRORS = ['TypeError: Load failed'];

/**
 * Adds automatic warning logs to a wrapped function.
 * @warn DO NOT WRAP FUNCTIONS THAT CONTAIN PII DATA, SINCE THE ARGS ARE SENT TO SENTRY!
 */
export function withLogs<Args extends any[], R>(callback: SyncFn<Args, R>, callbackName?: string): SyncFn<Args, R>;
export function withLogs<Args extends any[], R>(callback: AsyncFn<Args, R>, callbackName?: string): AsyncFn<Args, R>;
export function withLogs<Args extends any[], R>(callback: AnyFn<Args, R>, callbackName?: string): AnyFn<Args, R> {
    const name = callbackName || callback.name || '<anonymous>';
    return (...args: Args): R | Promise<R> => {
        try {
            const result = callback(...args);
            if (result instanceof Promise) {
                return result.catch((error) => {
                    if (error instanceof Error && OMIT_ERRORS.includes(error.message)) {
                        throw error;
                    }
                    console.error(error);
                    Sentry.captureMessage(`'${name}' function error: ${JSON.stringify(error)}`, {
                        level: 'warning',
                        extra: {
                            args,
                            error,
                        },
                    });
                    throw error;
                });
            }
            return result;
        } catch (error) {
            if (error instanceof Error && OMIT_ERRORS.includes(error.message)) {
                throw error;
            }
            console.error(error);
            Sentry.captureMessage(`'${name}' function error: ${JSON.stringify(error)}`, {
                level: 'warning',
                extra: {
                    args,
                    error,
                },
            });
            throw error;
        }
    };
}

type EventParams = {
    eventName: string;
    eventCategory: 'navigation' | 'interaction' | 'system' | 'error';
    eventPage?: string;
    userId?: string;
    boothId?: number;
    sessionId: string;
    properties?: Record<string, unknown>;
    source: 'kiosk' | 'floor' | 'home' | 'admin';
    // Controls dedup scope:
    // - omit: one event per second (default)
    // - 'session': one per session + event + page combo (good for page views)
    // - 'once': one per event + source + booth lifetime (good for one-time events)
    dedup?: 'session' | 'once';
};

const buildIdempotencyKey = (params: EventParams): string => {
    const base = `${params.source}-${params.boothId ?? 'na'}-${params.eventName}-${params.eventPage ?? 'na'}`;

    switch (params.dedup) {
        // One per session: page views, session-level events
        case 'session':
            return `${base}-${params.sessionId ?? 'na'}`;

        // One ever per source+booth: first-time setup, onboarding
        case 'once':
            return base;

        // Default: one per second, blocks re-render duplicates
        default: {
            const seconds = Math.floor(Date.now() / 1000);
            return `${base}-${seconds}`;
        }
    }
};

export const trackEvent = (client: SupabaseClient<Database>, params: EventParams): void => {
    client
        .from('event_logs')
        .upsert(
            {
                event_name: params.eventName,
                event_category: params.eventCategory,
                event_page: params.eventPage,
                user_id: params.userId,
                booth_id: params.boothId,
                session_id: params.sessionId!,
                properties: params.properties ?? ({} as Record<string, any>),
                source: params.source,
                idempotency_key: buildIdempotencyKey(params),
            },
            { onConflict: 'idempotency_key', ignoreDuplicates: true },
        )
        .then(({ error }) => {
            if (error) {
                Logger.error(`trackEvent failed: ${error.message}`);
            }
        });
};
